// HR Module Routes - AgentBheem ERP
// Unified routes for sidebar menu integration
import { Suspense, lazy, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useWorkspaceStore, type CanvasType } from './stores'
import { AgentLoader } from '@/components/ui/AgentLoader'

// Lazy load the embedded workspace (fits within DashboardLayout)
const EmbeddedWorkspace = lazy(() =>
  import('./workspace/EmbeddedWorkspace').then((m) => ({ default: m.EmbeddedWorkspace }))
)

// Loading fallback - centered in viewport
function HRLoader() {
  return (
    <div className="h-[calc(100vh-12rem)] flex items-center justify-center">
      <AgentLoader size="lg" text="Loading HR Module..." />
    </div>
  )
}

// Canvas route mapping - supports multiple path patterns
const canvasMap: Record<string, CanvasType> = {
  // Direct paths (from sidebar menu)
  employee: 'employee-workspace',
  'employee-page': 'employee-page',
  employees: 'employees',
  'employee-workspace': 'employee-workspace',
  payroll: 'payroll',
  recruitment: 'recruitment',
  hiring: 'recruitment',
  statutory: 'statutory',
  compliance: 'statutory',
  attendance: 'attendance',
  leave: 'leaves',
  leaves: 'leaves',
  dashboard: 'dashboard',
}

// Wrapper to sync URL with workspace state
function HRWorkspaceWrapper({ canvas }: { canvas?: string }) {
  const setActiveCanvas = useWorkspaceStore((s) => s.setActiveCanvas)

  useEffect(() => {
    if (canvas && canvasMap[canvas]) {
      setActiveCanvas(canvasMap[canvas])
    } else if (!canvas) {
      setActiveCanvas('dashboard')
    }
  }, [canvas, setActiveCanvas])

  return (
    <Suspense fallback={<HRLoader />}>
      <EmbeddedWorkspace />
    </Suspense>
  )
}

// Employee profile wrapper
function EmployeeProfileWrapper() {
  const { employeeId } = useParams<{ employeeId: string }>()
  const navigateToEmployee = useWorkspaceStore((s) => s.navigateToEmployee)

  useEffect(() => {
    if (employeeId) {
      navigateToEmployee(employeeId)
    }
  }, [employeeId, navigateToEmployee])

  return (
    <Suspense fallback={<HRLoader />}>
      <EmbeddedWorkspace />
    </Suspense>
  )
}

// HR Routes configuration (nested under /dashboard)
// These routes integrate with the sidebar menu items
export const hrRoutes = [
  // HR Dashboard (main entry point)
  {
    path: 'hr',
    element: <HRWorkspaceWrapper />,
  },
  // HR sub-routes
  {
    path: 'hr/employee',
    element: <HRWorkspaceWrapper canvas="employee" />,
  },
  {
    path: 'hr/employee-page',
    element: <HRWorkspaceWrapper canvas="employee-page" />,
  },
  {
    path: 'hr/employee-workspace',
    element: <HRWorkspaceWrapper canvas="employee-workspace" />,
  },
  {
    path: 'hr/employees',
    element: <HRWorkspaceWrapper canvas="employees" />,
  },
  {
    path: 'hr/employees/:employeeId',
    element: <EmployeeProfileWrapper />,
  },
  {
    path: 'hr/payroll',
    element: <HRWorkspaceWrapper canvas="payroll" />,
  },
  {
    path: 'hr/recruitment',
    element: <HRWorkspaceWrapper canvas="recruitment" />,
  },
  {
    path: 'hr/statutory',
    element: <HRWorkspaceWrapper canvas="statutory" />,
  },
  {
    path: 'hr/attendance',
    element: <HRWorkspaceWrapper canvas="attendance" />,
  },
  {
    path: 'hr/leave',
    element: <HRWorkspaceWrapper canvas="leave" />,
  },

  // ===== DIRECT PATHS (for menu items without /hr prefix) =====
  {
    path: 'employee',
    element: <HRWorkspaceWrapper canvas="employee" />,
  },
  {
    path: 'employee-page',
    element: <HRWorkspaceWrapper canvas="employee-page" />,
  },
  {
    path: 'employee-workspace',
    element: <HRWorkspaceWrapper canvas="employee-workspace" />,
  },
  {
    path: 'employees',
    element: <HRWorkspaceWrapper canvas="employees" />,
  },
  {
    path: 'employees/:employeeId',
    element: <EmployeeProfileWrapper />,
  },
  {
    path: 'payroll',
    element: <HRWorkspaceWrapper canvas="payroll" />,
  },
  {
    path: 'recruitment',
    element: <HRWorkspaceWrapper canvas="recruitment" />,
  },
  {
    path: 'statutory',
    element: <HRWorkspaceWrapper canvas="statutory" />,
  },
  {
    path: 'attendance',
    element: <HRWorkspaceWrapper canvas="attendance" />,
  },
  {
    path: 'leave',
    element: <HRWorkspaceWrapper canvas="leave" />,
  },
]

// Root-level HR routes (for /hr/* paths outside /dashboard)
// These are relative paths (no leading slash) to be nested under /hr
export const hrRootRoutes = [
  {
    index: true,
    element: <HRWorkspaceWrapper />,
  },
  {
    path: 'employee',
    element: <HRWorkspaceWrapper canvas="employee" />,
  },
  {
    path: 'employee-page',
    element: <HRWorkspaceWrapper canvas="employee-page" />,
  },
  {
    path: 'employee-workspace',
    element: <HRWorkspaceWrapper canvas="employee-workspace" />,
  },
  {
    path: 'employees',
    element: <HRWorkspaceWrapper canvas="employees" />,
  },
  {
    path: 'employees/:employeeId',
    element: <EmployeeProfileWrapper />,
  },
  {
    path: 'payroll',
    element: <HRWorkspaceWrapper canvas="payroll" />,
  },
  {
    path: 'recruitment',
    element: <HRWorkspaceWrapper canvas="recruitment" />,
  },
  {
    path: 'statutory',
    element: <HRWorkspaceWrapper canvas="statutory" />,
  },
  {
    path: 'attendance',
    element: <HRWorkspaceWrapper canvas="attendance" />,
  },
  {
    path: 'leave',
    element: <HRWorkspaceWrapper canvas="leave" />,
  },
]
